import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad from './SignaturePad.jsx';
import {
  confirmPhotoUpload, deviceInfo, getSignRequest, newClientRequestId, putPhoto, receiptHref, requestPhotoUpload,
  submitSignature,
} from './signPortalApi.js';
import { resizePhoto } from './photoResize.js';
import {
  PROBLEM_KINDS, buildSignBody, lineProblemKey, finalScreen, firstName, fmtWhen, formProblem, kindText, lineCount, lineQty,
  loadErrorState, metaLine, orderLine, palletSummary, receiptOffered, statusState, totalsLine, vendorLine, vendorMeta,
} from './SignPortal.rules.js';

/**
 * SignPortal — the receipt a customer, receiver or driver signs (jobq-api /portal/sign/*).
 *
 * Opened from a QR on any phone (/sign/<token>, pages/SignPortalPage.jsx), or EMBEDDED in the
 * shop's own screen ("Sign on this device instead"): then it fills the box it is given (no
 * 100vh, no document title, no window scrolling), says signed_on 'operator_device', and calls
 * onDone(result) when the person taps Done on a final screen.
 *
 * Props: token (required), embedded = false, onDone(result), onClose() (optional; embedded only:
 * shows a Close button in the header).
 * onDone's result: {status: 'signed', signed: {name, at}, receipt_url?, already?: true} after a
 * signature (already = it was signed before this screen opened), or {status: 'expired' | 'void' |
 * 'changed' | 'notfound' | 'unavailable'} when the person closes a screen that can't be signed.
 *
 * Never shows a price, a job number or a PIN: it draws only the fields it names.
 * One client_request_id per screen: a retry after a dropped connection sends the SAME id, so the
 * API replays the stored result instead of signing twice.
 */

const C = {
  ink: '#0f172a',
  muted: '#475569',
  soft: '#64748b',
  line: '#e2e8f0',
  faint: '#f1f5f9',
  page: '#f8fafc',
  blue: '#1d4ed8',
  green: '#166534',
  greenBg: '#dcfce7',
  red: '#991b1b',
  redBg: '#fee2e2',
  redLine: '#fca5a5',
  amberBg: '#fef3c7',
  amber: '#92400e',
};

const FONT = 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const S = {
  shell: (embedded) => ({
    boxSizing: 'border-box',
    background: C.page,
    color: C.ink,
    fontFamily: FONT,
    fontSize: 16,
    lineHeight: 1.4,
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    ...(embedded ? { minHeight: '100%' } : { minHeight: '100vh' }),
  }),
  header: {
    background: C.ink,
    color: '#fff',
    padding: '16px 20px 14px',
    paddingTop: 'max(16px, env(safe-area-inset-top))',
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
  },
  brand: { fontSize: 13, color: '#cbd5e1', fontWeight: 600, letterSpacing: '0.04em' },
  title: { fontSize: 22, fontWeight: 700, margin: '2px 0 0' },
  sub: { fontSize: 14, color: '#cbd5e1', overflowWrap: 'anywhere' },
  body: { flex: 1, padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14, width: '100%', maxWidth: 560, margin: '0 auto', boxSizing: 'border-box' },
  card: { background: '#fff', border: `1px solid ${C.line}`, borderRadius: 12, padding: '12px 14px' },
  cardHead: { fontWeight: 700, marginBottom: 4 },
  row: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderTop: `1px solid ${C.faint}` },
  fld: { display: 'flex', flexDirection: 'column', gap: 6, fontWeight: 600, fontSize: 15 },
  input: {
    minHeight: 48, border: '1px solid #cbd5e1', borderRadius: 10, padding: '0 12px', fontSize: 16, fontFamily: FONT,
    color: C.ink, background: '#fff', boxSizing: 'border-box', width: '100%',
  },
  textarea: {
    minHeight: 72, border: '1px solid #cbd5e1', borderRadius: 10, padding: '10px 12px', fontSize: 16, fontFamily: FONT,
    color: C.ink, background: '#fff', boxSizing: 'border-box', width: '100%', resize: 'vertical',
  },
  btn: {
    minHeight: 52, padding: '0 16px', borderRadius: 10, border: '1px solid #cbd5e1', background: '#fff', fontSize: 16,
    fontWeight: 600, fontFamily: FONT, color: C.ink, cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box', width: '100%',
  },
  primary: { background: C.blue, borderColor: C.blue, color: '#fff' },
  busy: { opacity: 0.7, cursor: 'progress' },
  link: {
    background: 'none', border: 0, color: C.blue, fontSize: 15, fontWeight: 600, fontFamily: FONT, minHeight: 48,
    padding: '0 4px', cursor: 'pointer', textAlign: 'left',
  },
  mini: (on) => ({
    minHeight: 48, minWidth: 48, padding: '0 12px', borderRadius: 10, fontSize: 15, fontWeight: 600, fontFamily: FONT,
    cursor: 'pointer', border: `1px solid ${on ? C.redLine : '#cbd5e1'}`, background: on ? C.redBg : '#fff',
    color: on ? C.red : C.ink, flexShrink: 0,
  }),
  chip: (on) => ({
    minHeight: 48, padding: '0 14px', borderRadius: 24, fontSize: 15, fontWeight: 600, fontFamily: FONT, cursor: 'pointer',
    border: `1px solid ${on ? C.red : '#cbd5e1'}`, background: on ? C.redBg : '#fff', color: on ? C.red : C.ink,
  }),
  footer: {
    position: 'sticky', bottom: 0, background: '#fff', borderTop: `1px solid ${C.line}`, padding: '12px 20px',
    paddingBottom: 'max(16px, env(safe-area-inset-bottom))', zIndex: 1,
  },
  footerInner: { maxWidth: 560, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 8 },
  alert: { borderRadius: 10, padding: '10px 12px', fontSize: 15, background: C.redBg, color: C.red, border: `1px solid ${C.redLine}` },
  note: { fontSize: 14, color: C.muted, lineHeight: 1.45 },
  center: { flex: 1, padding: '32px 24px', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 14, maxWidth: 520, margin: '0 auto', boxSizing: 'border-box', width: '100%' },
  bigIcon: (tone) => ({
    width: 72, height: 72, borderRadius: 36, background: tone === 'ok' ? C.greenBg : C.amberBg, display: 'flex',
    alignItems: 'center', justifyContent: 'center',
  }),
  thumbs: { display: 'flex', flexWrap: 'wrap', gap: 10 },
  thumb: { width: 96, display: 'flex', flexDirection: 'column', gap: 4 },
  thumbImg: { width: 96, height: 96, borderRadius: 8, objectFit: 'cover', background: '#cbd5e1', display: 'block' },
  addPhoto: {
    width: 96, height: 96, borderRadius: 8, border: '2px dashed #94a3b8', background: '#fff', color: C.blue, fontSize: 15,
    fontWeight: 600, fontFamily: FONT, cursor: 'pointer',
  },
  srOnly: { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' },
};

const OK_PATH = 'M5 12l5 5L20 7';
const WARN_PATH = 'M12 8v5M12 17h.01M10.3 3.9L2.4 18a2 2 0 0 0 1.7 3h15.8a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z';

function Icon({ path, color, size = 20, width = 2.6 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={width}
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flexShrink: 0 }}>
      <path d={path} />
    </svg>
  );
}

function Header({ kind, data, embedded, onClose }) {
  const k = kindText(kind);
  const order = data && data.order;
  const vendor = kind === 'vendor_receipt';
  // a vendor receipt (an outsourcing paper) names the paper and who sent it where, never an order or a driver
  const sub = vendor ? vendorLine(data && data.manifest) : orderLine(order);
  const meta = vendor ? vendorMeta(data && data.manifest) : metaLine(order, data && data.load);
  return (
    <header style={S.header}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={S.brand}>KANCRAFT MANUFACTURING</div>
        <h1 style={S.title}>{k.title}</h1>
        {sub && <div style={S.sub}>{sub}</div>}
        {meta && <div style={S.sub}>{meta}</div>}
      </div>
      {embedded && onClose && (
        <button type="button" onClick={onClose}
          style={{ ...S.mini(false), background: 'transparent', color: '#fff', borderColor: '#475569' }}>
          Close
        </button>
      )}
    </header>
  );
}

let photoSeq = 0;

export default function SignPortal({ token, embedded = false, onDone, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadState, setLoadState] = useState(null); // a final screen for a failed GET
  const [overrideState, setOverrideState] = useState(null); // 'changed' | 'expired' | ... after a refused sign
  const [result, setResult] = useState(null); // the signed answer
  const [alreadySigned, setAlreadySigned] = useState(null); // a 409 already_signed's {name, at, method?}

  const [name, setName] = useState('');
  const [deliveredTo, setDeliveredTo] = useState('');
  const [problems, setProblems] = useState({}); // {pallet_id: {kind, note}}
  const [lineProblems, setLineProblems] = useState({}); // {'pallet:line': {kind, note}} (a vendor receipt)
  const [problemNote, setProblemNote] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [showLines, setShowLines] = useState(false);
  const [hasSig, setHasSig] = useState(false);
  const [geo, setGeo] = useState({ state: 'idle', value: null });
  const [photos, setPhotos] = useState([]); // {key, status, preview, upload_id, file, message}
  const [formError, setFormError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [retryNote, setRetryNote] = useState(null);

  const padRef = useRef(null);
  const rootRef = useRef(null);
  const requestIdRef = useRef(null);
  if (!requestIdRef.current) requestIdRef.current = newClientRequestId();
  const mountedRef = useRef(true);
  const doneCalledRef = useRef(false);
  const previewsRef = useRef([]);

  useEffect(() => {
    mountedRef.current = true;
    const previews = previewsRef.current; // the same array for the page's life
    return () => {
      mountedRef.current = false;
      previews.forEach((u) => { try { URL.revokeObjectURL(u); } catch (e) { /* ignore */ } });
    };
  }, []);

  const load = useCallback(async () => {
    if (!token) {
      setLoadState('notfound');
      setLoading(false);
      return null;
    }
    setLoading(true);
    setLoadState(null);
    try {
      const body = await getSignRequest(token);
      if (!mountedRef.current) return null;
      setData(body);
      return body;
    } catch (e) {
      if (mountedRef.current) setLoadState(loadErrorState(e));
      return null;
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const kind = (data && data.kind) || 'pickup';
  const k = kindText(kind);
  const ask = (data && data.ask) || {};
  const pallets = useMemo(() => (data && Array.isArray(data.pallets) ? data.pallets : []), [data]);

  useEffect(() => {
    if (embedded || typeof document === 'undefined') return undefined;
    const before = document.title;
    document.title = `${k.title} · KanCraft`;
    return () => { document.title = before; };
  }, [embedded, k.title]);

  const scrollTop = () => {
    if (embedded) {
      if (rootRef.current && rootRef.current.scrollIntoView) rootRef.current.scrollIntoView({ block: 'start' });
    } else if (typeof window !== 'undefined' && window.scrollTo) {
      try { window.scrollTo(0, 0); } catch (e) { /* jsdom */ }
    }
  };

  const anyProblem = Object.values(problems).some(Boolean) || Object.values(lineProblems).some(Boolean)
    || problemNote.trim().length > 0;

  const finish = (payload) => {
    if (doneCalledRef.current) return;
    doneCalledRef.current = true;
    if (onDone) onDone(payload);
  };

  // ---- photos (delivery) -------------------------------------------------------------------
  const maxPhotos = Number(ask.max_photos) || 10;

  const updatePhoto = (key, patch) => {
    if (!mountedRef.current) return;
    setPhotos((list) => list.map((p) => (p.key === key ? { ...p, ...patch } : p)));
  };

  const uploadOne = async (key, file) => {
    updatePhoto(key, { status: 'uploading', message: null });
    try {
      const shrunk = await resizePhoto(file);
      if (typeof URL !== 'undefined' && URL.createObjectURL) {
        const preview = URL.createObjectURL(shrunk.blob);
        previewsRef.current.push(preview);
        updatePhoto(key, { preview });
      }
      const up = await requestPhotoUpload(token, {
        filename: shrunk.filename, content_type: shrunk.content_type, bytes: shrunk.blob.size,
      });
      await putPhoto(up.upload_url, shrunk.blob, up.headers);
      const done = await confirmPhotoUpload(token, up.upload_id);
      if (done && done.status === 'failed') {
        updatePhoto(key, { status: 'failed', upload_id: null, message: "That photo couldn't be used." });
      } else {
        updatePhoto(key, { status: 'ready', upload_id: up.upload_id });
      }
    } catch (e) {
      updatePhoto(key, { status: 'failed', upload_id: null, message: e && e.message ? e.message : 'Did not upload.' });
    }
  };

  const addPhotos = async (fileList) => {
    const files = Array.from(fileList || []);
    const room = Math.max(0, maxPhotos - photos.length);
    const take = files.slice(0, room);
    const items = take.map((file) => {
      photoSeq += 1;
      return { key: `p${photoSeq}`, status: 'uploading', preview: null, upload_id: null, file, message: null };
    });
    if (!items.length) return;
    setPhotos((list) => [...list, ...items]);
    for (const it of items) {
      // one at a time: a dock's one bar of signal shouldn't carry five uploads at once
      // eslint-disable-next-line no-await-in-loop
      await uploadOne(it.key, it.file);
    }
  };

  const removePhoto = (key) => setPhotos((list) => list.filter((p) => p.key !== key));

  // ---- location ----------------------------------------------------------------------------
  const canGeo = !!ask.geolocation && typeof navigator !== 'undefined' && !!navigator.geolocation;
  const askLocation = () => {
    if (!canGeo) return;
    setGeo({ state: 'asking', value: null });
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (!mountedRef.current) return;
        const c = pos.coords || {};
        setGeo({
          state: 'saved',
          value: {
            lat: c.latitude, lon: c.longitude, accuracy_m: c.accuracy != null ? Math.round(c.accuracy) : null,
            at: new Date(pos.timestamp || Date.now()).toISOString(),
          },
        });
      },
      () => { if (mountedRef.current) setGeo({ state: 'denied', value: null }); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 60000 },
    );
  };

  // ---- problems ----------------------------------------------------------------------------
  const toggleProblem = (pid) => setProblems((cur) => {
    const next = { ...cur };
    if (next[pid]) delete next[pid];
    else next[pid] = { kind: null, note: '' };
    return next;
  });
  const setProblem = (pid, patch) => setProblems((cur) => ({ ...cur, [pid]: { ...(cur[pid] || {}), ...patch } }));
  const toggleLineProblem = (lid) => setLineProblems((cur) => {
    const next = { ...cur };
    if (next[lid]) delete next[lid];
    else next[lid] = { kind: null, note: '' };
    return next;
  });
  const setLineProblem = (lid, patch) => setLineProblems((cur) => ({ ...cur, [lid]: { ...(cur[lid] || {}), ...patch } }));

  // ---- sign --------------------------------------------------------------------------------
  const submit = async () => {
    if (submitting) return;
    const pad = padRef.current;
    const sigOk = !!pad && !pad.isEmpty();
    const problem = formProblem({ name, hasSignature: sigOk, problems, lineProblems, photos });
    if (problem) { setFormError(problem); return; }
    const png = pad.toPngBase64();
    if (!png) { setFormError("Your signature couldn't be read. Clear it and sign again."); return; }
    setFormError(null);
    setRetryNote(null);
    setSubmitting(true);
    const body = buildSignBody({
      clientRequestId: requestIdRef.current,
      name,
      signaturePng: png,
      ask,
      deliveredTo,
      problemNote,
      problems,
      lineProblems,
      pallets,
      photos,
      geo: geo.value,
      device: deviceInfo(),
      embedded,
    });
    try {
      const res = await submitSignature(token, body);
      if (!mountedRef.current) return;
      setResult({
        status: 'signed',
        signed: (res && res.signed) || { name: body.signer_name, at: new Date().toISOString() },
        receipt_url: res && res.receipt_url,
        has_problem: anyProblem,
        copy: res && res.copy && typeof res.copy === 'object' ? res.copy : null,
        copy_message: (res && res.copy_message) || null,
      });
      scrollTop();
    } catch (e) {
      if (!mountedRef.current) return;
      const status = e && e.status;
      const detail = e && e.detail;
      if (status === 409 && detail && typeof detail === 'object' && detail.error === 'already_signed') {
        // Someone (or a paper signature at the shop) already signed for these pallets: say who, not "changed".
        setAlreadySigned(detail.signed && typeof detail.signed === 'object' ? detail.signed : {});
        setOverrideState('signed');
        scrollTop();
      } else if (status === 409 || status === 410) {
        const fresh = await load();
        if (!mountedRef.current) return;
        // Signed by someone else / expired / void: the fresh GET says so. Still pending (or no
        // answer): the shop changed the pallets under this code.
        if (!statusState(fresh)) setOverrideState(status === 410 ? 'expired' : 'changed');
        scrollTop();
      } else if (status === 501 || (status === 503 && e.detail)) {
        setOverrideState('unavailable');
        scrollTop();
      } else if (e && e.retryable) {
        setRetryNote('No connection. Nothing is lost: tap Try again.');
      } else if (status === 429) {
        setRetryNote('Too many tries. Wait a minute, then tap Try again.');
      } else {
        setFormError((e && e.message) || 'That did not go through. Please try again.');
      }
    } finally {
      if (mountedRef.current) setSubmitting(false);
    }
  };

  // ---- screens -----------------------------------------------------------------------------
  const shell = (children, footer) => (
    <div ref={rootRef} style={S.shell(embedded)} data-testid="sign-portal">
      <Header kind={kind} data={data} embedded={embedded} onClose={onClose} />
      {children}
      {footer}
    </div>
  );

  if (loading && !data && !loadState) {
    return shell(
      <main style={S.center} aria-busy="true">
        <div style={S.note}>Loading your receipt…</div>
      </main>,
    );
  }

  if (result) {
    const who = result.signed && result.signed.name;
    const when = fmtWhen(result.signed && result.signed.at);
    let title = `Thanks, ${firstName(who) || 'all set'}`;
    const copyMsg = result.copy_message;
    let text = copyMsg
      ? `Signed ${when}. ${copyMsg}`
      : `Signed ${when}. The shop will hand you a printed copy, and we'll email one to the order contact.`;
    if (kind === 'delivery') {
      title = 'Delivered';
      text = `Signed by ${who}${when ? ` on ${when}` : ''}. `
        + (copyMsg || 'KanCraft has the signed receipt, and a copy goes to your order contact.');
      if (result.has_problem) text += " We noted the problem and the shop has been told.";
    } else if (kind === 'carrier_handoff') {
      text = `Signed ${when}. ${copyMsg || 'KanCraft keeps the signed copy.'}`;
    } else if (kind === 'vendor_receipt') {
      title = 'Received';
      text = `Signed by ${who}${when ? ` on ${when}` : ''}. `
        + (copyMsg || 'KanCraft has the signed paperwork.');
      if (result.has_problem) text += ' We noted the problem and KanCraft has been told.';
    }
    const copyFiled = !(result.copy && result.copy.filed === false);
    return shell(
      <main style={S.center}>
        <div style={S.bigIcon('ok')}><Icon path={OK_PATH} color={C.green} size={40} /></div>
        <h2 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>{title}</h2>
        <div style={{ color: '#334155', lineHeight: 1.5 }} role="status">{text}</div>
        {!embedded && copyFiled && (
          <a style={{ ...S.btn, marginTop: 8 }} href={receiptHref(token, result.receipt_url)} target="_blank" rel="noopener noreferrer">
            Download my copy (PDF)
          </a>
        )}
        <div style={{ color: C.muted, fontSize: 14 }}>This code can't be signed again.</div>
        {embedded && (
          <button type="button" style={{ ...S.btn, ...S.primary }} onClick={() => finish(result)}>Done</button>
        )}
      </main>,
    );
  }

  const final = overrideState || loadState || statusState(data);
  if (final) {
    // A 409 already_signed names who signed for the pallets (on this code or another record, maybe on paper).
    const signedInfo = (final === 'signed' && alreadySigned) || (data && data.signed) || null;
    const view = finalScreen(final, { kind, signed: signedInfo });
    const signedAt = signedInfo && signedInfo.at;
    // This code's own copy exists only when THIS code was signed (a pallet record's method = another signing).
    const offerReceipt = final === 'signed' && !embedded && receiptOffered(signedAt)
      && !(alreadySigned && alreadySigned.method);
    return shell(
      <main style={S.center}>
        <div style={S.bigIcon(view.tone)}>
          <Icon path={view.tone === 'ok' ? OK_PATH : WARN_PATH} color={view.tone === 'ok' ? C.green : C.amber} size={40} />
        </div>
        <h2 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>{view.title}</h2>
        <div style={{ color: '#334155', lineHeight: 1.5 }} role="status">{view.body}</div>
        {offerReceipt && (
          <a style={S.btn} href={receiptHref(token)} target="_blank" rel="noopener noreferrer">Download my copy (PDF)</a>
        )}
        {view.retry && (
          <button type="button" style={{ ...S.btn, ...S.primary, ...(loading ? S.busy : null) }} disabled={loading}
            onClick={() => load()}>
            {loading ? 'Trying…' : 'Try again'}
          </button>
        )}
        {embedded && (
          <button type="button" style={S.btn}
            onClick={() => (final === 'signed'
              ? finish({ status: 'signed', signed: signedInfo, already: true })
              : (onClose ? onClose() : finish({ status: final })))}>
            {final === 'signed' ? 'Done' : 'Close'}
          </button>
        )}
      </main>,
    );
  }

  // ---- the form ----------------------------------------------------------------------------
  const lines = lineCount(pallets);
  const totals = totalsLine(data && data.totals, pallets);
  const vendor = kind === 'vendor_receipt';
  const linesOpen = showLines || vendor; // a vendor checks every part: the lines are always shown
  const photosBusy = photos.some((p) => p.status === 'uploading');

  const form = (
    <main style={S.body}>
      <section style={S.card} aria-labelledby="sp-pallets">
        <div id="sp-pallets" style={S.cardHead}>{k.listHeading}</div>
        {pallets.map((p) => {
          const flagged = problems[p.pallet_id];
          // a vendor paper's "Not on a pallet" group has no pallet id: its lines can be flagged, the group can't
          const flaggable = ask.pallet_problems && p.pallet_id !== null && p.pallet_id !== undefined;
          return (
            <div key={p.pallet_id ?? `loose-${p.short_id}`} style={{ borderTop: `1px solid ${C.faint}` }}>
              <div style={{ ...S.row, borderTop: 0 }}>
                {flaggable && (
                  <Icon path={flagged ? WARN_PATH : OK_PATH} color={flagged ? '#b91c1c' : C.green} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <b>{p.short_id || `Pallet ${p.pallet_id}`}</b>
                  {palletSummary(p) && <div style={{ fontSize: 14, color: C.muted }}>{palletSummary(p)}</div>}
                </div>
                {flaggable && (
                  <button type="button" style={S.mini(!!flagged)}
                    aria-label={flagged ? `Never mind: no problem with ${p.short_id || 'this pallet'}` : undefined}
                    onClick={() => toggleProblem(p.pallet_id)}>
                    {flagged ? 'Never mind' : 'Problem?'}
                  </button>
                )}
              </div>
              {flaggable && flagged && (
                <div style={{ padding: '0 0 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <div role="radiogroup" aria-label={`What's wrong with ${p.short_id || 'this pallet'}?`}
                    style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {PROBLEM_KINDS.map((opt) => (
                      <button key={opt.value} type="button" role="radio" aria-checked={flagged.kind === opt.value}
                        style={S.chip(flagged.kind === opt.value)} onClick={() => setProblem(p.pallet_id, { kind: opt.value })}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <input style={S.input} value={flagged.note || ''} maxLength={500}
                    aria-label={`Note about ${p.short_id || 'this pallet'} (optional)`} placeholder="What's wrong? (optional)"
                    onChange={(e) => setProblem(p.pallet_id, { note: e.target.value })} />
                </div>
              )}
              {vendor && Array.isArray(p.lines) && p.lines.length > 0 && (
                <ul style={{ listStyle: 'none', margin: '0 0 10px', padding: 0, fontSize: 14, color: '#334155' }}>
                  {p.lines.map((ln, i) => {
                    const lk = ln.line_id ? lineProblemKey(p.pallet_id, ln.line_id) : null;
                    const lf = lk ? lineProblems[lk] : null;
                    const label = ln.part || ln.description || 'this part';
                    return (
                      <li key={`${ln.line_id || 'l'}-${i}`} style={{ padding: '6px 0', borderTop: i ? `1px solid ${C.faint}` : 0 }}>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <span style={{ fontWeight: 700, minWidth: 44 }}>{lineQty(ln)}</span>
                          <span style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
                            <b>{ln.part}</b>{ln.description ? `${ln.part ? ' · ' : ''}${ln.description}` : ''}
                            {ln.finish && <span style={{ display: 'block', fontSize: 13, color: C.muted }}>{ln.finish}</span>}
                          </span>
                          {ask.line_problems && ln.line_id && (
                            <button type="button" style={S.mini(!!lf)} onClick={() => toggleLineProblem(lk)}
                              aria-label={lf ? `Never mind: no problem with ${label}` : `Problem with ${label}?`}>
                              {lf ? 'Never mind' : 'Problem?'}
                            </button>
                          )}
                        </div>
                        {lf && (
                          <div style={{ padding: '8px 0 4px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div role="radiogroup" aria-label={`What's wrong with ${label}?`}
                              style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              {PROBLEM_KINDS.map((opt) => (
                                <button key={opt.value} type="button" role="radio" aria-checked={lf.kind === opt.value}
                                  style={S.chip(lf.kind === opt.value)} onClick={() => setLineProblem(lk, { kind: opt.value })}>
                                  {opt.label}
                                </button>
                              ))}
                            </div>
                            <input style={S.input} value={lf.note || ''} maxLength={500}
                              aria-label={`Note about ${label} (optional)`} placeholder="How many, or what's wrong? (optional)"
                              onChange={(e) => setLineProblem(lk, { note: e.target.value })} />
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
              {!vendor && linesOpen && Array.isArray(p.lines) && p.lines.length > 0 && (
                <ul style={{ listStyle: 'none', margin: '0 0 10px', padding: 0, fontSize: 14, color: '#334155' }}>
                  {p.lines.map((ln, i) => (
                    <li key={`${ln.product_id}-${i}`} style={{ padding: '4px 0', display: 'flex', gap: 8 }}>
                      <span style={{ fontWeight: 700, minWidth: 40 }}>{ln.qty}×</span>
                      <span style={{ overflowWrap: 'anywhere' }}>
                        <b>{ln.product_id}</b>{ln.description ? ` · ${ln.description}` : ''}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
        {totals && <div style={{ fontSize: 14, color: C.muted, paddingTop: 8, borderTop: `1px solid ${C.faint}` }}>{totals}</div>}
        {lines > 0 && !vendor && (
          <button type="button" style={S.link} aria-expanded={showLines} onClick={() => setShowLines((v) => !v)}>
            {showLines ? 'Hide the parts' : `See every part (${lines} ${lines === 1 ? 'line' : 'lines'})`}
          </button>
        )}
      </section>

      <label style={S.fld}>
        {k.nameLabel}
        <input style={S.input} value={name} onChange={(e) => setName(e.target.value)} autoComplete="name"
          autoCapitalize="words" maxLength={100} enterKeyHint="next" />
      </label>

      {ask.delivered_to && (
        <label style={S.fld}>
          Delivered to (dock, door or area)
          <input style={S.input} value={deliveredTo} onChange={(e) => setDeliveredTo(e.target.value)} autoComplete="off"
            maxLength={200} />
        </label>
      )}

      {ask.received_note && (
        <label style={S.fld}>
          Where you received it (optional)
          <input style={S.input} value={deliveredTo} onChange={(e) => setDeliveredTo(e.target.value)} autoComplete="off"
            placeholder="Dock, door or area" maxLength={200} />
        </label>
      )}

      {canGeo && (
        <div>
          {geo.state === 'saved' ? (
            <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: C.green, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, padding: '12px' }}>
              <Icon path="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z" color={C.green} size={18} width={2} />
              Location added
            </div>
          ) : (
            <button type="button" style={S.btn} onClick={askLocation} disabled={geo.state === 'asking'}>
              {geo.state === 'asking' ? 'Finding your location…' : 'Add my location (optional)'}
            </button>
          )}
          {geo.state === 'denied' && (
            <div style={{ ...S.note, marginTop: 6 }} role="status">Location not shared. That's fine: you can still sign.</div>
          )}
        </div>
      )}

      {ask.photos && (
        <div style={S.fld}>
          <span>Photos (optional)</span>
          <div style={S.thumbs}>
            {photos.map((ph, i) => (
              <div key={ph.key} style={S.thumb}>
                {ph.preview
                  ? <img src={ph.preview} alt={`Upload ${i + 1}`} style={{ ...S.thumbImg, opacity: ph.status === 'uploading' ? 0.5 : 1 }} />
                  : <div style={S.thumbImg} aria-label={`Upload ${i + 1}`} />}
                <div style={{ fontSize: 13, fontWeight: 500, color: ph.status === 'failed' ? C.red : C.muted }} role="status">
                  {ph.status === 'uploading' ? 'Uploading…' : ph.status === 'failed' ? (ph.message || 'Did not upload') : 'Added'}
                </div>
                {ph.status === 'failed' && (
                  <button type="button" style={{ ...S.mini(false), fontSize: 14 }} onClick={() => uploadOne(ph.key, ph.file)}>Try again</button>
                )}
                {ph.status !== 'uploading' && (
                  <button type="button" style={{ ...S.mini(false), fontSize: 14 }} onClick={() => removePhoto(ph.key)}
                    aria-label={`Remove photo ${i + 1}`}>
                    Remove
                  </button>
                )}
              </div>
            ))}
            {photos.length < maxPhotos && (
              <label style={{ ...S.addPhoto, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                Add photo
                <input type="file" accept="image/*" multiple style={S.srOnly} data-testid="photo-input"
                  onChange={(e) => { const f = e.target.files; addPhotos(f); e.target.value = ''; }} />
              </label>
            )}
          </div>
        </div>
      )}

      <div style={S.fld}>
        <span id="sp-sig-label">{k.sigLabel}</span>
        <div style={{ border: '2px dashed #94a3b8', borderRadius: 12, background: '#fff', position: 'relative' }}>
          <SignaturePad ref={padRef} height={kind === 'pickup' ? 170 : 150} onChange={setHasSig} disabled={submitting} />
          {!hasSig && (
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontWeight: 500, pointerEvents: 'none' }}>
              Sign here
            </span>
          )}
        </div>
        <button type="button" style={{ ...S.link, alignSelf: 'flex-end' }} disabled={submitting}
          onClick={() => padRef.current && padRef.current.clear()}>
          Clear signature
        </button>
      </div>

      {ask.pallet_problems && (
        showNote || problemNote ? (
          <label style={S.fld}>
            Anything else wrong? (optional)
            <textarea style={S.textarea} value={problemNote} maxLength={500} onChange={(e) => setProblemNote(e.target.value)} />
          </label>
        ) : (
          <button type="button" style={S.link} onClick={() => setShowNote(true)}>Something else wrong? Add a note</button>
        )
      )}

      <div style={S.note}>
        {(data && data.attestation) || k.attestation}
        {kind === 'pickup' && !ask.pallet_problems && ' Something wrong? Tell the person at the shop before you sign.'}
      </div>
    </main>
  );

  const footer = (
    <div style={S.footer}>
      <div style={S.footerInner}>
        {formError && <div style={S.alert} role="alert">{formError}</div>}
        {retryNote && <div style={{ ...S.alert, background: C.amberBg, color: C.amber, borderColor: '#fcd34d' }} role="alert">{retryNote}</div>}
        <button type="button" onClick={submit} disabled={submitting || photosBusy}
          style={{ ...S.btn, ...((hasSig && name.trim().length >= 2) ? S.primary : { background: '#e2e8f0', borderColor: '#cbd5e1', color: '#334155' }), ...(submitting ? S.busy : null) }}>
          {submitting ? 'Sending…' : retryNote ? 'Try again' : photosBusy ? 'Waiting for photos…' : k.submit(anyProblem)}
        </button>
      </div>
    </div>
  );

  return shell(form, footer);
}
